import { toast } from '@/lib/toast';
import { voiceMedia } from '@/lib/voiceMedia';
import { getSocket } from '@/lib/socket';
import { clearVoiceIntent, setVoiceIntent, voiceTabId } from '@/lib/voiceIntent';
import { getPreDeaf, getPreMute, setPreDeaf, setPreMute } from '@/lib/voicePrefs';
import { initialVoiceSelfState } from '@/lib/voiceSelfState';
import { primeAudioContext } from '@/lib/voice/audioGraph';
import { humanizeVoiceJoinError } from '@/components/contentPane/voiceErrors';
import i18n from '@/i18n';
import { useDataStore } from '@/stores/dataStore';
import { canIn, PERMISSIONS } from '@/lib/guildPermissions';
import { speakBlockedIn } from '@/lib/voiceRights';

/** The server channel with this id among my guilds, or null (a DM call). */
function findGuildChannel(channelId: string) {
  for (const guild of Object.values(useDataStore.getState().guilds)) {
    const channel = guild.channels.find((c) => c.id === channelId);
    if (channel) return channel;
  }
  return null;
}

/**
 * Voice control commands shared between the voice-room sticky bar in
 * ContentPane and the drawer footer profile strip in ChannelDrawer.
 * Both surfaces used to ship near-identical 60-line copies of these
 * functions — the rules-of-Discord ("deaf implies mute") logic plus the
 * screen-share picker error-timing heuristic. Centralising means a
 * future fix (e.g. retry policy for screen-share denied) lands in one
 * place.
 *
 * Each command is a thin orchestration: voiceMedia (audio plumbing) +
 * socket.io ack (server-side state). All read live `voiceMedia` snapshot
 * — no React state here.
 */

export interface VoiceToggleContext {
  /** True when the local user is currently joined to a voice channel. */
  inVoice: boolean;
  /** Current self-mute state from the voice store; ignored when !inVoice. */
  selfMuted: boolean;
  /** Current self-deafen state from the voice store; ignored when !inVoice. */
  selfDeafened: boolean;
  /** Server-side mute (a moderator) — the user can't lift it themselves. */
  serverMute: boolean;
  /** Server-side deafen (a moderator) — the user can't lift it themselves. */
  serverDeaf: boolean;
}

/**
 * One mic and one sound state per person, kept in voicePrefs: the same value
 * out of voice (what you walk in with), in a call, across channel switches
 * and reconnects. Deafening doesn't flip the mic; it only stops transmitting
 * (voiceMedia), so undeafening leaves the mic exactly as the user left it —
 * the old «deafen mutes, undeafen unmutes» turned a mic the user had muted
 * on its own back on. While deafened the mic shows as off everywhere
 * (micShownOff), so pressing the mic then means «I want to talk»: it brings
 * the sound back and turns the mic on, the way Discord does.
 *
 * A moderator's mute/deafen can't be lifted by the user: the toggle then says
 * so instead of silently doing nothing.
 */
export function toggleSelfMute(ctx: VoiceToggleContext): void {
  if (ctx.inVoice && ctx.serverMute) {
    toast.message(i18n.t('userWidget.micServerMuted'));
    return;
  }
  // The room withholds SPEAK: the server holds the microphone whatever the
  // toggle says, so say why instead of flipping an icon that changes nothing.
  if (ctx.inVoice && speakBlockedIn(useDataStore.getState())) {
    toast.message(i18n.t('voice.rights.speakLost'));
    return;
  }
  const deafened = ctx.inVoice ? ctx.selfDeafened || ctx.serverDeaf : getPreDeaf();
  if (deafened) {
    if (ctx.inVoice && ctx.serverDeaf) {
      toast.message(i18n.t('userWidget.deafServerDeafened'));
      return;
    }
    setPreDeaf(false);
    setPreMute(false);
    if (ctx.inVoice) {
      voiceMedia.setDeafened(false);
      voiceMedia.setMuted(false);
      getSocket().emit('voice:state', { selfDeaf: false, selfMute: false });
    }
    return;
  }
  const next = !(ctx.inVoice ? ctx.selfMuted : getPreMute());
  setPreMute(next);
  if (ctx.inVoice) {
    voiceMedia.setMuted(next);
    getSocket().emit('voice:state', { selfMute: next });
  }
}

export function toggleSelfDeaf(ctx: VoiceToggleContext): void {
  if (ctx.inVoice && ctx.serverDeaf) {
    toast.message(i18n.t('userWidget.deafServerDeafened'));
    return;
  }
  const next = !(ctx.inVoice ? ctx.selfDeafened : getPreDeaf());
  setPreDeaf(next);
  if (ctx.inVoice) {
    voiceMedia.setDeafened(next);
    getSocket().emit('voice:state', { selfDeaf: next });
  }
}

/**
 * Toggle screen-share. The browser native picker is wrapped with:
 *   - A hint-toast while the picker is up ("Выбери окно..." — first-time
 *     users don't realise the OS dialog they're seeing is the picker).
 *   - A heuristic on a fast `permission_denied`: if it fires in < 300ms
 *     we know the iframe / Permissions-Policy blocked the picker from
 *     even appearing, so we surface a different error ("открой в
 *     отдельной вкладке").
 *
 * Returns true on success, false if the user cancelled / browser blocked
 * (caller can decide whether to surface a banner).
 */
export interface ScreenShareCallbacks {
  /** Called with a humanised error string when the picker fails with
   *  anything OTHER than `permission_denied` (which is just "user said no"). */
  onError?: (message: string) => void;
}

export async function toggleSelfScreen(
  ctx: { inVoice: boolean; screenOn: boolean },
  cb: ScreenShareCallbacks = {},
): Promise<boolean> {
  if (!ctx.inVoice) return false;
  const next = !ctx.screenOn;
  const hintId = next
    ? toast.message(i18n.t('lib2.screen.pickTitle'), {
        duration: 8000,
      })
    : null;
  const t0 = performance.now();
  try {
    await voiceMedia.setScreenShareEnabled(next);
  } catch (err) {
    if (hintId) toast.dismiss(hintId);
    const elapsed = performance.now() - t0;
    const msg = err instanceof Error ? err.message : 'screen_error';
    if (msg === 'screen_permission_denied' && elapsed < 300) {
      toast.error(i18n.t('lib2.screen.blockedTitle'), {
        description: i18n.t('lib2.screen.blockedDesc'),
        duration: 6000,
      });
    } else if (msg !== 'screen_permission_denied') {
      cb.onError?.(msg);
    }
    return false;
  }
  if (hintId) toast.dismiss(hintId);
  getSocket().emit('voice:state', { screenOn: next });
  return true;
}

export interface JoinVoiceCallbacks {
  /** Fired while the join is in flight (true) and when it settles (false). */
  onPending?: (pending: boolean) => void;
  /** Humanised error string on failure / timeout. */
  onError?: (message: string) => void;
  /**
   * The channel is a tournament room and its on-air notice has not been
   * accepted. Separate from `onError` because it is not a failure — it is a
   * question, and the caller should ask it rather than show a red line.
   */
  onConsentRequired?: (channelId: string) => void;
}

/**
 * Join a voice channel (Discord-style: a single click connects). Centralises
 * the socket `voice:join` handshake — sets the reconnect intent, fires the
 * ack with an 8s timeout (so a dead server doesn't leave the UI stuck), and
 * reports pending/error through callbacks so any surface (sidebar click,
 * voice-stub button) can drive its own UI without duplicating the protocol.
 */
export function joinVoice(channelId: string, cb: JoinVoiceCallbacks = {}): void {
  // A room I may not enter: say so now, before a microphone prompt and a
  // round-trip that could only end in a refusal.
  if (!canIn(findGuildChannel(channelId), PERMISSIONS.CONNECT_VOICE)) {
    cb.onError?.(i18n.t('lib2.join.notAllowed'));
    return;
  }
  // Resume the mic-processing AudioContext NOW, synchronously inside the
  // click. By the time the audio graph is built the activation is spent (the
  // `voice:join` round-trip + a first-timer's mic permission prompt run
  // first), and a suspended context silently sends a muted track — the
  // «нового участника не слышно, пока не перезайдёт» bug.
  primeAudioContext();
  const socket = getSocket();
  if (!socket.connected) socket.connect();
  setVoiceIntent(channelId);
  cb.onPending?.(true);
  let settled = false;
  const timer = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    cb.onPending?.(false);
    clearVoiceIntent();
    cb.onError?.(
      socket.connected
        ? i18n.t('lib2.join.noResponse')
        : i18n.t('lib2.join.noConnection'),
    );
  }, 8000);
  socket.emit(
    'voice:join',
    { channelId, ...initialVoiceSelfState(), tab: voiceTabId() },
    (result?: { ok?: boolean; error?: string }) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cb.onPending?.(false);
      if (!result?.ok) {
        clearVoiceIntent();
        if (result?.error === 'broadcast_consent_required') {
          cb.onConsentRequired?.(channelId);
          return;
        }
        cb.onError?.(humanizeVoiceJoinError(result?.error));
      }
    },
  );
}

/**
 * Disconnect from the current voice channel. Clears voice intent (so a
 * socket reconnect won't rejoin) + tears down media + tells the server.
 */
export function leaveVoice(): void {
  clearVoiceIntent();
  voiceMedia.leave();
  getSocket().emit('voice:leave');
}
